import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Pagination,
  Snackbar,
  Stack,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";
import {
  cancelProduction,
  getProduction,
  getProductions,
} from "../api/productionsApi";
import CancelProductionDialog from "../components/CancelProductionDialog";
import ProductionDetailDialog from "../components/ProductionDetailDialog";
import ProductionFilters from "../components/ProductionFilters";
import ProductionsTable from "../components/ProductionsTable";
import {
  normalizeProduction,
  normalizeProductionList,
} from "../utils/productionModel";
import { getProductionErrorMessage } from "../utils/productionError";

const PAGE_SIZE = 20;

function CardShell({ children, sx }) {
  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "neutral.300",
        borderRadius: "30px",
        boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.5)",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export default function ProduccionElaboradosPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const latestRequestId = useRef(0);
  const [productions, setProductions] = useState([]);
  const [filters, setFilters] = useState({ dateFrom: "", dateTo: "" });
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadVersion, setReloadVersion] = useState(0);
  const [selectedProduction, setSelectedProduction] = useState(null);
  const [productionDetail, setProductionDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailRetryVersion, setDetailRetryVersion] = useState(0);
  const [productionToCancel, setProductionToCancel] = useState(null);
  const [cancellingProductionId, setCancellingProductionId] = useState(null);
  const [cancelError, setCancelError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const message = location.state?.successMessage;
    if (!message) return;
    setSuccessMessage(message);
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [location.pathname, location.search, location.state?.successMessage, navigate]);

  const invalidDateRange = Boolean(
    filters.dateFrom && filters.dateTo && filters.dateFrom > filters.dateTo
  );

  const fetchProductions = useCallback(
    async (signal) => {
      const requestId = ++latestRequestId.current;
      if (invalidDateRange) {
        setProductions([]);
        setTotalItems(0);
        setTotalPages(0);
        setLoading(false);
        setError("La fecha desde no puede ser posterior a la fecha hasta.");
        return;
      }

      setLoading(true);
      setError("");
      try {
        const response = await getProductions({
          page,
          limit: PAGE_SIZE,
          dateFrom: filters.dateFrom || undefined,
          dateTo: filters.dateTo || undefined,
          signal,
        });
        if (signal?.aborted || requestId !== latestRequestId.current) return;

        const result = normalizeProductionList(response);
        if (result.meta.totalPages > 0 && page > result.meta.totalPages) {
          setPage(result.meta.totalPages);
          return;
        }
        setProductions(result.data.slice(0, PAGE_SIZE));
        setTotalItems(result.meta.totalItems);
        setTotalPages(result.meta.totalPages);
      } catch (requestError) {
        if (signal?.aborted || requestId !== latestRequestId.current) return;
        setProductions([]);
        setTotalItems(0);
        setTotalPages(0);
        setError(
          getProductionErrorMessage(
            requestError,
            "No se pudieron cargar las producciones."
          )
        );
      } finally {
        if (!signal?.aborted && requestId === latestRequestId.current) {
          setLoading(false);
        }
      }
    },
    [filters.dateFrom, filters.dateTo, invalidDateRange, page]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchProductions(controller.signal);
    return () => controller.abort();
  }, [fetchProductions, reloadVersion]);

  useEffect(() => {
    if (!selectedProduction?.id) return undefined;
    const controller = new AbortController();
    setDetailLoading(true);
    setDetailError("");

    getProduction(selectedProduction.id, { signal: controller.signal })
      .then((response) => {
        if (!controller.signal.aborted) {
          setProductionDetail(normalizeProduction(response));
        }
      })
      .catch((requestError) => {
        if (!controller.signal.aborted) {
          setProductionDetail(null);
          setDetailError(
            getProductionErrorMessage(
              requestError,
              "No se pudo cargar el detalle de la producción."
            )
          );
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setDetailLoading(false);
      });

    return () => controller.abort();
  }, [selectedProduction?.id, detailRetryVersion]);

  const pageSummary = useMemo(() => {
    if (!totalItems) return "Sin producciones para mostrar";
    return `Mostrando ${productions.length} de ${totalItems} producciones`;
  }, [productions.length, totalItems]);

  function handleFilterChange(name, value) {
    setFilters((current) => ({ ...current, [name]: value }));
    setPage(1);
  }

  function handleOpenDetail(production) {
    setSelectedProduction(production);
    setProductionDetail(null);
    setDetailError("");
  }

  function handleCloseDetail() {
    setSelectedProduction(null);
    setProductionDetail(null);
    setDetailError("");
  }

  function handleOpenCancellation(production) {
    if (cancellingProductionId) return;
    setProductionToCancel(production);
    setCancelError("");
  }

  function handleCloseCancellation() {
    if (cancellingProductionId) return;
    setProductionToCancel(null);
    setCancelError("");
  }

  async function handleConfirmCancellation() {
    if (!productionToCancel?.id || cancellingProductionId) return;
    const productionId = productionToCancel.id;
    setCancellingProductionId(productionId);
    setCancelError("");
    try {
      const response = await cancelProduction(productionId);
      const cancelled = normalizeProduction(response);
      setProductions((current) =>
        current.map((production) =>
          production.id === productionId ? cancelled : production
        )
      );
      if (selectedProduction?.id === productionId) {
        setSelectedProduction(cancelled);
        setProductionDetail(cancelled);
      }
      setProductionToCancel(null);
      setSuccessMessage("La producción fue anulada correctamente.");
    } catch (requestError) {
      setCancelError(
        getProductionErrorMessage(
          requestError,
          "No se pudo anular la producción."
        )
      );
    } finally {
      setCancellingProductionId(null);
    }
  }

  return (
    <Stack
      spacing={2}
      sx={{
        p: 2,
        flex: 1,
        minHeight: 0,
        overflow: { xs: "auto", md: "hidden" },
      }}
    >
      <StockPageHeader
        title="Producción de Elaborados"
        onBack={() => navigate("/stock/operacion")}
      />

      <CardShell sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
        <Stack spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            sx={{
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
            }}
          >
            <ProductionFilters
              {...filters}
              onChange={handleFilterChange}
            />

            <Button
              type="button"
              onClick={() =>
                navigate("/stock/operacion/produccion-elaborados/crear")
              }
              sx={{
                alignSelf: { xs: "stretch", sm: "flex-end", md: "center" },
                minWidth: 150,
                height: 38,
                px: 2.4,
                borderRadius: "33px",
                bgcolor: "primary.500",
                color: "logoblanco",
                textTransform: "none",
                fontSize: 12,
                fontWeight: 800,
                boxShadow: "0px 4px 12px rgba(23, 31, 76, 0.18)",
                "&:hover": { bgcolor: "primary.600" },
              }}
            >
              Producir
            </Button>
          </Stack>

          {error ? (
            <Box
              role="alert"
              sx={{
                px: 2,
                py: 1.2,
                borderRadius: "18px",
                bgcolor: "secondary.700",
                boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.35)",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between" }}
              >
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: "logoblanco" }}>
                    No se pudo completar la operación
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, color: "logoblanco", opacity: 0.95 }}>
                    {error}
                  </Typography>
                </Box>
                {!invalidDateRange ? (
                  <Button
                    size="small"
                    onClick={() => setReloadVersion((value) => value + 1)}
                    sx={{ color: "logoblanco", textTransform: "none", fontWeight: 800 }}
                  >
                    Reintentar
                  </Button>
                ) : null}
              </Stack>
            </Box>
          ) : null}

          <ProductionsTable
            rows={productions}
            loading={loading || Boolean(cancellingProductionId)}
            onView={handleOpenDetail}
            onCancel={handleOpenCancellation}
            emptyMessage={
              filters.dateFrom || filters.dateTo
                ? "No se encontraron producciones para las fechas seleccionadas."
                : "Todavía no hay producciones registradas."
            }
          />

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.2}
            sx={{
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              pt: 0.5,
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.900" }}>
              {pageSummary}
            </Typography>
            <Pagination
              page={page}
              count={Math.max(totalPages, 1)}
              onChange={(_, value) => setPage(value)}
              shape="rounded"
              color="primary"
              disabled={loading || totalPages <= 1}
            />
          </Stack>
        </Stack>
      </CardShell>

      <ProductionDetailDialog
        open={Boolean(selectedProduction)}
        detail={productionDetail}
        loading={detailLoading}
        error={detailError}
        onRetry={() => setDetailRetryVersion((value) => value + 1)}
        onClose={handleCloseDetail}
      />

      <CancelProductionDialog
        open={Boolean(productionToCancel)}
        production={productionToCancel}
        loading={cancellingProductionId === productionToCancel?.id}
        error={cancelError}
        onClose={handleCloseCancellation}
        onConfirm={handleConfirmCancellation}
      />

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={3500}
        onClose={(_, reason) => {
          if (reason !== "clickaway") setSuccessMessage("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSuccessMessage("")}
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
